import { useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type TrainingRecordDto, type CourseDto } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { Badge } from '@/components/ui/badge'
import { Breadcrumb } from '@/components/shared/Breadcrumb'
import { useTopBar } from '@/contexts/TopBarContext'
import { CertificateViewerModal } from '@/components/shared/CertificateViewerModal'
import { Plus, Upload, Eye, Download, Trash2, RefreshCw, ShieldCheck } from 'lucide-react'
import { usePagination } from '@/hooks/usePagination'
import { TablePagination } from '@/components/shared/TablePagination'

function RecordForm({
  initial,
  courses,
  onSubmit,
  onCancel,
  pending,
}: {
  initial?: Partial<TrainingRecordDto>
  courses: CourseDto[]
  onSubmit: (data: { courseId: string; completedDate: string; expiryDate: string | null }) => void
  onCancel: () => void
  pending: boolean
}) {
  const [courseId, setCourseId] = useState(initial?.courseId ?? '')
  const [completedDate, setCompletedDate] = useState(initial?.completedDate?.slice(0, 10) ?? '')
  const [expiryDate, setExpiryDate] = useState(initial?.expiryDate?.slice(0, 10) ?? '')

  const handleCourseChange = (newCourseId: string) => {
    setCourseId(newCourseId)
    // Auto-fill expiry date from course's default if not already set
    if (!expiryDate && completedDate) {
      const course = courses.find(c => c.id === newCourseId)
      if (course?.defaultExpiryMonths) {
        const completed = new Date(completedDate)
        completed.setMonth(completed.getMonth() + course.defaultExpiryMonths)
        setExpiryDate(completed.toISOString().slice(0, 10))
      }
    }
  }

  const handleCompletedDateChange = (date: string) => {
    setCompletedDate(date)
    // If a course with default expiry is selected, recalculate expiry
    if (courseId && date) {
      const course = courses.find(c => c.id === courseId)
      if (course?.defaultExpiryMonths) {
        const completed = new Date(date)
        completed.setMonth(completed.getMonth() + course.defaultExpiryMonths)
        setExpiryDate(completed.toISOString().slice(0, 10))
      }
    }
  }

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        onSubmit({ courseId, completedDate, expiryDate: expiryDate || null })
      }}
      className="flex gap-4 items-end flex-wrap"
    >
      <div className="space-y-1 flex-1 min-w-48">
        <Label htmlFor="courseId">Course</Label>
        <select
          id="courseId"
          value={courseId}
          onChange={e => handleCourseChange(e.target.value)}
          required
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <option value="">Select a course…</option>
          {courses.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1 min-w-36">
        <Label htmlFor="completedDate">Completed</Label>
        <Input id="completedDate" type="date" value={completedDate} onChange={e => handleCompletedDateChange(e.target.value)} required />
      </div>
      <div className="space-y-1 min-w-36">
        <Label htmlFor="expiryDate">Expiry (optional)</Label>
        <Input id="expiryDate" type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={pending}>{pending ? 'Saving…' : 'Save'}</Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  )
}

export default function TrainingRecordsPage() {
  const { clientId, staffId } = useParams<{ clientId: string; staffId: string }>()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadingFor, setUploadingFor] = useState<string | null>(null)

  const [showAdd, setShowAdd] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [viewerUrl, setViewerUrl] = useState<string | null>(null)
  const [viewerContentType, setViewerContentType] = useState('application/pdf')
  const [viewerTitle, setViewerTitle] = useState('')

  const { data: client } = useQuery({
    queryKey: ['clients', clientId],
    queryFn: () => adminApi.getClient(clientId!).then(r => r.data),
    enabled: !!clientId,
  })

  const { data: staffList = [] } = useQuery({
    queryKey: ['staff', clientId],
    queryFn: () => adminApi.getStaff(clientId!).then(r => r.data),
    enabled: !!clientId,
  })

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })

  const staffMember = staffList.find(s => s.id === staffId)
  const staffName = staffMember ? `${staffMember.firstName} ${staffMember.lastName}` : '…'

  useTopBar(
    <Breadcrumb items={[
      { label: 'Clients', to: '/admin/clients' },
      { label: client?.name ?? '…', to: `/admin/clients/${clientId}` },
      { label: staffName },
    ]} />
  )

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['training-records', staffId],
    queryFn: () => adminApi.getTrainingRecords(staffId!).then(r => r.data),
    enabled: !!staffId,
  })
  const { paged, page, setPage, totalPages, total, pageSize, start } = usePagination(records)

  const addMutation = useMutation({
    mutationFn: (data: { courseId: string; completedDate: string; expiryDate: string | null }) =>
      adminApi.addTrainingRecord(staffId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-records', staffId] })
      setShowAdd(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { courseId: string; completedDate: string; expiryDate: string | null } }) =>
      adminApi.updateTrainingRecord(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-records', staffId] })
      setEditingId(null)
    },
  })

  const uploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => adminApi.uploadCertificate(id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['training-records', staffId] }),
  })

  const deleteCertMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteCertificate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['training-records', staffId] }),
  })

  const regenerateMutation = useMutation({
    mutationFn: (id: string) => adminApi.regenerateCertificate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['training-records', staffId] }),
    onError: (err: any) => {
      const msg = err?.response?.data?.detail ?? err?.response?.data?.title ?? 'Failed to generate certificate.'
      alert(msg)
    },
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && uploadingFor) uploadMutation.mutate({ id: uploadingFor, file })
    e.target.value = ''
    setUploadingFor(null)
  }

  const handleView = async (id: string, courseName: string) => {
    const res = await adminApi.streamCertificate(id)
    const blob: Blob = res.data
    const blobUrl = URL.createObjectURL(blob)
    setViewerUrl(prev => { if (prev) URL.revokeObjectURL(prev); return blobUrl })
    setViewerContentType(blob.type || 'application/octet-stream')
    setViewerTitle(courseName)
  }

  const handleDownload = async (id: string) => {
    const res = await adminApi.getCertificateUrl(id)
    window.open(res.data.url, '_blank')
  }

  const triggerUpload = (id: string) => {
    setUploadingFor(id)
    fileInputRef.current?.click()
  }

  return (
    <>
    {viewerUrl && (
      <CertificateViewerModal
        blobUrl={viewerUrl}
        contentType={viewerContentType}
        title={viewerTitle}
        onClose={() => {
          URL.revokeObjectURL(viewerUrl)
          setViewerUrl(null)
        }}
      />
    )}
    <div>
      <div className="flex items-center justify-between mb-6">
        <div />
        <Button size="sm" className="gap-2" onClick={() => setShowAdd(v => !v)}>
          <Plus size={16} /> Add record
        </Button>
      </div>

      <input ref={fileInputRef} type="file" accept="application/pdf" className="hidden" onChange={handleFileChange} />

      {showAdd && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <RecordForm
              courses={courses}
              onSubmit={data => addMutation.mutate(data)}
              onCancel={() => setShowAdd(false)}
              pending={addMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {isLoading && <p className="text-center text-gray-500 py-8">Loading…</p>}
        {!isLoading && records.length === 0 && (
          <p className="text-center text-gray-500 py-8">No training records yet.</p>
        )}
        {paged.map(record => (
          <div key={record.id}>
            {editingId === record.id ? (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <RecordForm
                  initial={record}
                  courses={courses}
                  onSubmit={data => updateMutation.mutate({ id: record.id, data })}
                  onCancel={() => setEditingId(null)}
                  pending={updateMutation.isPending}
                />
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-gray-900">{record.courseName}</p>
                  {record.isSuperseded ? <Badge variant="neutral">Superseded</Badge> : <StatusBadge status={record.status} />}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-gray-500">
                  <span>Completed: {record.completedDate.slice(0, 10)}</span>
                  {record.expiryDate && <span>Expires: {record.expiryDate.slice(0, 10)}</span>}
                </div>
                <div className="mt-3 flex items-center gap-1 flex-wrap">
                  {record.hasCertificate ? (
                    <>
                      <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs text-slate-500"
                        onClick={() => handleView(record.id, record.courseName)}>
                        <Eye size={13} /> View
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700"
                        title="Download" onClick={() => handleDownload(record.id)}>
                        <Download size={13} />
                      </Button>
                      {record.courseIsThirdPartyCertified && record.certificateId && (
                        <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-xs text-slate-500" asChild>
                          <a href={`/verify/${record.certificateId}`} target="_blank" rel="noreferrer" title="Open the public verification page">
                            <ShieldCheck size={13} /> Verify
                          </a>
                        </Button>
                      )}
                      {!record.courseIsThirdPartyCertified && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-indigo-400 hover:text-indigo-600"
                          title="Regenerate"
                          onClick={() => regenerateMutation.mutate(record.id)}
                          disabled={regenerateMutation.isPending && regenerateMutation.variables === record.id}>
                          <RefreshCw size={13} className={regenerateMutation.isPending && regenerateMutation.variables === record.id ? 'animate-spin' : ''} />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                        title="Remove certificate" onClick={() => deleteCertMutation.mutate(record.id)}>
                        <Trash2 size={13} />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-gray-500"
                        onClick={() => triggerUpload(record.id)}
                        disabled={uploadMutation.isPending && uploadingFor === record.id}>
                        <Upload size={13} /> Upload
                      </Button>
                      {!record.courseIsThirdPartyCertified && (
                        <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-indigo-500 hover:text-indigo-700"
                          onClick={() => regenerateMutation.mutate(record.id)}
                          disabled={regenerateMutation.isPending && regenerateMutation.variables === record.id}>
                          <RefreshCw size={13} className={regenerateMutation.isPending && regenerateMutation.variables === record.id ? 'animate-spin' : ''} />
                          Generate
                        </Button>
                      )}
                    </>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 ml-auto text-xs text-gray-400"
                    onClick={() => setEditingId(record.id)}>
                    Edit
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block">
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Course</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Certificate</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">Loading…</TableCell>
                </TableRow>
              )}
              {!isLoading && records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">No training records yet.</TableCell>
                </TableRow>
              )}
              {paged.map(record => (
                editingId === record.id ? (
                  <TableRow key={record.id}>
                    <TableCell colSpan={6} className="py-3">
                      <RecordForm
                        initial={record}
                        courses={courses}
                        onSubmit={data => updateMutation.mutate({ id: record.id, data })}
                        onCancel={() => setEditingId(null)}
                        pending={updateMutation.isPending}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={record.id}>
                    <TableCell className="font-medium">{record.courseName}</TableCell>
                    <TableCell className="text-gray-600">{record.completedDate.slice(0, 10)}</TableCell>
                    <TableCell className="text-gray-600">{record.expiryDate ? record.expiryDate.slice(0, 10) : '—'}</TableCell>
                    <TableCell>{record.isSuperseded ? <Badge variant="neutral">Superseded</Badge> : <StatusBadge status={record.status} />}</TableCell>
                    <TableCell>
                      {record.hasCertificate ? (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1.5 text-slate-500"
                            onClick={() => handleView(record.id, record.courseName)}
                          >
                            <Eye size={14} /> View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-slate-700"
                            title="Download certificate"
                            onClick={() => handleDownload(record.id)}
                          >
                            <Download size={14} />
                          </Button>
                          {record.courseIsThirdPartyCertified && record.certificateId && (
                            <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-slate-500" asChild>
                              <a href={`/verify/${record.certificateId}`} target="_blank" rel="noreferrer" title="Open the public verification page">
                                <ShieldCheck size={14} /> Verify
                              </a>
                            </Button>
                          )}
                          {!record.courseIsThirdPartyCertified && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-indigo-400 hover:text-indigo-600"
                              title="Regenerate certificate from template"
                              onClick={() => regenerateMutation.mutate(record.id)}
                              disabled={regenerateMutation.isPending && regenerateMutation.variables === record.id}
                            >
                              <RefreshCw size={14} className={regenerateMutation.isPending && regenerateMutation.variables === record.id ? 'animate-spin' : ''} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                            title="Remove certificate"
                            onClick={() => deleteCertMutation.mutate(record.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 text-gray-500"
                            onClick={() => triggerUpload(record.id)}
                            disabled={uploadMutation.isPending && uploadingFor === record.id}
                          >
                            <Upload size={14} /> Upload
                          </Button>
                          {!record.courseIsThirdPartyCertified && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1 text-indigo-500 hover:text-indigo-700"
                              title="Generate certificate from template"
                              onClick={() => regenerateMutation.mutate(record.id)}
                              disabled={regenerateMutation.isPending && regenerateMutation.variables === record.id}
                            >
                              <RefreshCw size={14} className={regenerateMutation.isPending && regenerateMutation.variables === record.id ? 'animate-spin' : ''} />
                              Generate
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-gray-400"
                        onClick={() => setEditingId(record.id)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              ))}
            </TableBody>
          </Table>
          <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
        </Card>
      </div>
    </div>
    </>
  )
}
